/**
 * Excel Utility Functions
 * Helper functions for data transformation and formatting
 */

/**
 * Format date to DD-MM-YYYY
 */
export const formatDate = (date) => {
  const formattedDate = new Date(date);
  const day = String(formattedDate.getDate()).padStart(2, "0");
  const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
  const year = formattedDate.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Transform backend data to Excel format
 * Maps backend field names to display column names
 */
export const transformDataForExcel = (data, columnOrder) => {
  if (!data || data.length === 0) return { columns: [], transformedData: [] };

  const columnMapping = {
    dist_name: "Name",
    total_block_count: "Total Blocks",
    report_submitted: "Report Submitted",
    report_not_submitted: "Report Not Submitted",
  };

  const columns = columnOrder.map((key) => ({
    label: columnMapping[key] || key.toUpperCase().replace(/_/g, " "),
    key: key,
  }));

  const transformedData = data.map((row) => {
    const transformedRow = {};
    columns.forEach((col) => {
      const value = row[col.key];
      transformedRow[col.label] = value !== null && value !== undefined ? value : "";
    });
    return transformedRow;
  });

  return { columns, transformedData };
};

/**
 * Get current timestamp in IST format for filename
 */
export const getTimestamp = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 330);
  const istDate = now.toISOString().split("T")[0];
  const istTime = now
    .toISOString()
    .split("T")[1]
    .split(".")[0]
    .replace(/:/g, "");
  return `${istDate}_${istTime}`;
};

export const formatDateForMISExcel = (dateValue) => {
  if (!dateValue) return "";
  try {
    let date;
    if (typeof dateValue === "string") {
      // If already in DD-MM-YYYY format, return as-is
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
        return dateValue;
      }
      // If YYYY-MM-DD format, convert to DD-MM-YYYY
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-');
        return `${day}-${month}-${year}`;
      }
      date = new Date(dateValue);
    } else if (typeof dateValue === "number" && dateValue > 10000000000) {
      date = new Date(dateValue);
    } else if (typeof dateValue === "number") {
      date = new Date(dateValue * 1000);
    }
    
    if (!date || isNaN(date.getTime())) return "";
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;  // ✅ Returns DD-MM-YYYY
  } catch (e) {
    console.error("Date formatting error", e);
    return String(dateValue);
  }
};


/**
 * Transform MIS District Datewise Report data for Excel
 */
export const transformMISDataForExcel = (rawData) => {
  console.log('🔍 transformMISDataForExcel called');
  console.log('- Input rawData type:', typeof rawData);
  console.log('- Input rawData:', rawData);
  console.log('- Input rawData keys:', rawData ? Object.keys(rawData) : 'null');

  if (!rawData || typeof rawData !== 'object') {
    console.error('❌ transformMISDataForExcel: Invalid data');
    return { columns: [], transformedData: [] };
  }

  let misData, cumulativeData, dailyRecords, cumMaxRecords, cumCumRecords;

  if (Array.isArray(rawData)) {
    console.log('✅ Detected direct array structure (daily records)');
    dailyRecords = rawData;
    cumMaxRecords = [];
    cumCumRecords = [];
  } else if (rawData.mis_data && rawData.cumulative_data) {
    console.log('✅ Detected NEW structure (mis_data + cumulative_data)');
    misData = rawData.mis_data;
    cumulativeData = rawData.cumulative_data;
    dailyRecords = misData.dr_cold_wave || [];
    cumMaxRecords = misData.dr_cold_wave_max || [];
    cumCumRecords = cumulativeData.dr_cold_wave_cum || [];
  } else if (rawData.dr_cold_wave) {
    console.log('✅ Detected OLD flat structure (dr_cold_wave at root)');
    dailyRecords = rawData.dr_cold_wave || [];
    cumMaxRecords = rawData.dr_cold_wave_max || [];
    cumCumRecords = rawData.dr_cold_wave_cum || [];
  } else if (rawData.data) {
    console.log('✅ Detected wrapped structure (data key)');
    const innerData = rawData.data;
    if (innerData.mis_data) {
      misData = innerData.mis_data;
      cumulativeData = innerData.cumulative_data || {};
      dailyRecords = misData.dr_cold_wave || [];
      cumMaxRecords = misData.dr_cold_wave_max || [];
      cumCumRecords = cumulativeData.dr_cold_wave_cum || [];
    } else if (Array.isArray(innerData)) {
      dailyRecords = innerData;
      cumMaxRecords = [];
      cumCumRecords = [];
    }
  } else {
    console.error('❌ Unknown data structure!');
    console.error('Available keys:', Object.keys(rawData));
    return { columns: [], transformedData: [] };
  }

  console.log(`- Daily records: ${dailyRecords?.length || 0}`);
  console.log(`- Cum Max records: ${cumMaxRecords?.length || 0}`);
  console.log(`- Cum Cum records: ${cumCumRecords?.length || 0}`);

  if (!dailyRecords || dailyRecords.length === 0) {
    console.error('❌ No daily records found in any structure');
    return { columns: [], transformedData: [] };
  }

  console.log('✅ First daily record sample:', dailyRecords[0]);

  const filteredDailyRecords = dailyRecords.filter(row => {
    const distName = (row.dist_name || row.district_name || '').toString().trim().toUpperCase();
    const blockName = (row.block_name || '').toString().trim().toUpperCase();
    return distName !== 'TOTAL' && blockName !== 'TOTAL';
  });

  console.log(`🔍 Filtered ${dailyRecords.length - filteredDailyRecords.length} TOTAL rows from backend data`);
  console.log(`✅ Processing ${filteredDailyRecords.length} data rows (excluding backend TOTAL)`);

  dailyRecords = filteredDailyRecords;

  const fieldMapping = {
    'dist_name': 'District Name',
    'block_name': 'Block Name',
    'reported_on': 'Date',
    'number_of_bonfire_places': 'No. of Places where bonfire lit (Today)',
    'number_of_bonfire_places_cum': 'No. of Places where bonfire lit (Cum)',
    'quantity_of_wood_burnt': 'Qty. of wood burned in kg (Today)',
    'quantity_of_wood_burnt_till_now': 'Qty. of wood burned in kg till date (Cum)',
    'number_of_deaths': 'Loss of Life (Today)',
    'number_of_deaths_cum': 'Loss of Life (Cum)',
    'number_of_night_shelters': 'No. of Night Shelters (Today)',
    'number_of_night_shelters_cum': 'No. of Night Shelters till now (Cum)',
    'number_of_ppl_taking_shelter_in_night_shelters': 'No. of people taking shelter in night shelter (Today)',
    'number_of_ppl_taking_shelter_in_night_shelters_cum': 'No. of people taking shelter in night shelter till now (Cum)',
    'number_of_blankets_distributed': 'No. of blankets distributed (Today)',
    'number_of_blankets_distributed_cum': 'No. of blankets distributed till now (Cum)',
  };

  const excelColumnLabels = [
    "District Name",
    "Date",
    "Block Name",
    "No. of Places where bonfire lit (Today)",
    "No. of Places where bonfire lit (Cum)",
    "Qty. of wood burned in kg (Today)",
    "Qty. of wood burned in kg till date (Cum)",
    "Loss of Life (Today)",
    "Loss of Life (Cum)",
    "No. of Night Shelters (Today)",
    "No. of Night Shelters till now (Cum)",
    "No. of people taking shelter in night shelter (Today)",
    "No. of people taking shelter in night shelter till now (Cum)",
    "No. of blankets distributed (Today)",
    "No. of blankets distributed till now (Cum)"
  ];

  const columns = excelColumnLabels.map(label => ({ label, key: label }));

  const makeKey = (rec) => `${rec.block_name}_${rec.reported_on}`;
  const cumMaxMap = {};
  const cumCumMap = {};

  if (cumMaxRecords && cumMaxRecords.length > 0) {
    cumMaxRecords.forEach(rec => {
      cumMaxMap[makeKey(rec)] = rec;
    });
  }

  if (cumCumRecords && cumCumRecords.length > 0) {
    cumCumRecords.forEach(rec => {
      cumCumMap[makeKey(rec)] = rec;
    });
  }

  const transformedData = dailyRecords.map((dailyRec, idx) => {
    const key = makeKey(dailyRec);
    const cumMaxRec = cumMaxMap[key];
    const cumCumRec = cumCumMap[key];
    
    const transformedRow = {};
    
    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      
      if (apiField === 'reported_on') {
        transformedRow[displayName] = formatDateForMISExcel(dailyRec[apiField]);
      } else if (apiField.endsWith('_cum') || apiField === 'quantity_of_wood_burnt_till_now') {
        let value = dailyRec[apiField];
        
        if (value === null || value === undefined) {
          value = cumMaxRec?.[apiField];
        }
        
        if (value === null || value === undefined) {
          value = cumCumRec?.[apiField];
        }
        
        // ✅ REVERTED: Match Web UI - cumulative 0 shows as "-"
        transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : "-";
      } else {
        transformedRow[displayName] = dailyRec[apiField] !== undefined ? dailyRec[apiField] : '';
      }
    });
    
    if (idx === 0) {
      console.log('✅ Sample transformed row:', transformedRow);
    }
    
    return transformedRow;
  });

  transformedData.sort((a, b) => {
    const dateA = a['Date'] || '';
    const dateB = b['Date'] || '';
    return dateA.localeCompare(dateB);
  });

  if (transformedData.length > 0) {
    const totalRow = {};
    totalRow['District Name'] = 'TOTAL';

    if (transformedData[0].hasOwnProperty('Date')) {
      totalRow['Date'] = 'NA';
    }

    if (transformedData[0].hasOwnProperty('Block Name')) {
      totalRow['Block Name'] = 'NA';
    }

    excelColumnLabels.forEach((columnLabel) => {
      if (columnLabel === 'District Name' || columnLabel === 'Date' || columnLabel === 'Block Name') {
        return;
      }

      let sum = 0;
      let hasValidData = false;

      transformedData.forEach(row => {
        const value = row[columnLabel];
        if (value !== null && value !== undefined && value !== '-' && value !== '' && value !== 'NA' && !isNaN(value)) {
          sum += parseFloat(value);
          hasValidData = true;
        }
      });

      totalRow[columnLabel] = hasValidData ? sum : 'NA';
    });

    transformedData.push(totalRow);
    console.log('✅ Added TOTAL row to MIS data');
  }

  console.log(`✅ Transformed ${transformedData.length} rows for Excel (including TOTAL row)`);
  return { columns, transformedData };
};

/**
 * Transform MIS District Report (aggregated by district)
 */
export const transformMISDistrictData = (rawData) => {
  console.log('🔍 transformMISDistrictData called');
  console.log('🔍 - Input type:', typeof rawData);
  console.log('🔍 - Is array?:', Array.isArray(rawData));
  console.log('🔍 - First record:', rawData[0]);
  
  if (!rawData || typeof rawData !== 'object') {
    console.error('❌ Invalid district data');
    return { columns: [], transformedData: [] };
  }

  if (Array.isArray(rawData) && rawData.length > 0) {
    console.log('✅ District Report: Aggregating by block across date range');
    
    const filteredData = rawData.filter(row => {
      const distName = (row.dist_name || row.district_name || '').toString().trim().toUpperCase();
      const blockName = (row.block_name || '').toString().trim().toUpperCase();
      return distName !== 'TOTAL' && blockName !== 'TOTAL';
    });
    
    console.log(`✅ Processing ${filteredData.length} daily records`);

    const blockMap = {};
    
    filteredData.forEach(record => {
      const blockKey = `${record.dist_name}_${record.block_name}`;
      
      if (!blockMap[blockKey]) {
        blockMap[blockKey] = {
          dist_name: record.dist_name,
          block_name: record.block_name,
          number_of_bonfire_places: 0,
          number_of_bonfire_places_cum: 0,
          quantity_of_wood_burnt: 0,
          quantity_of_wood_burnt_till_now: 0,
          number_of_deaths: 0,
          number_of_deaths_cum: 0,
          number_of_night_shelters: 0,
          number_of_night_shelters_cum: 0,
          number_of_ppl_taking_shelter_in_night_shelters: 0,
          number_of_ppl_taking_shelter_in_night_shelters_cum: 0,
          number_of_blankets_distributed: 0,
          number_of_blankets_distributed_cum: 0
        };
      }
      
      const block = blockMap[blockKey];
      
      // Today values - SUM
      block.number_of_bonfire_places += (record.number_of_bonfire_places || 0);
      block.quantity_of_wood_burnt += (record.quantity_of_wood_burnt || 0);
      block.number_of_deaths += (record.number_of_deaths || 0);
      block.number_of_night_shelters += (record.number_of_night_shelters || 0);
      block.number_of_ppl_taking_shelter_in_night_shelters += (record.number_of_ppl_taking_shelter_in_night_shelters || 0);
      block.number_of_blankets_distributed += (record.number_of_blankets_distributed || 0);
      
      // Cum values - MAX
      block.number_of_bonfire_places_cum = Math.max(
        block.number_of_bonfire_places_cum,
        record.number_of_bonfire_places_cum || 0
      );
      block.quantity_of_wood_burnt_till_now = Math.max(
        block.quantity_of_wood_burnt_till_now,
        record.quantity_of_wood_burnt_till_now || 0
      );
      block.number_of_deaths_cum = Math.max(
        block.number_of_deaths_cum,
        record.number_of_deaths_cum || 0
      );
      block.number_of_night_shelters_cum = Math.max(
        block.number_of_night_shelters_cum,
        record.number_of_night_shelters_cum || 0
      );
      block.number_of_ppl_taking_shelter_in_night_shelters_cum = Math.max(
        block.number_of_ppl_taking_shelter_in_night_shelters_cum,
        record.number_of_ppl_taking_shelter_in_night_shelters_cum || 0
      );
      block.number_of_blankets_distributed_cum = Math.max(
        block.number_of_blankets_distributed_cum,
        record.number_of_blankets_distributed_cum || 0
      );
    });
    
    const aggregatedBlocks = Object.values(blockMap);
    console.log(`✅ Aggregated ${filteredData.length} daily records into ${aggregatedBlocks.length} blocks`);

    const fieldMapping = {
      'dist_name': 'District Name',
      'block_name': 'Block Name',
      'number_of_bonfire_places': 'No. of Places where bonfire lit (Today)',
      'number_of_bonfire_places_cum': 'No. of Places where bonfire lit (Cum)',
      'quantity_of_wood_burnt': 'Qty. of wood burned in kg (Today)',
      'quantity_of_wood_burnt_till_now': 'Qty. of wood burned in kg till date (Cum)',
      'number_of_deaths': 'Loss of Life (Today)',
      'number_of_deaths_cum': 'Loss of Life (Cum)',
      'number_of_night_shelters': 'No. of Night Shelters (Today)',
      'number_of_night_shelters_cum': 'No. of Night Shelters till now (Cum)',
      'number_of_ppl_taking_shelter_in_night_shelters': 'No. of people taking shelter in night shelter (Today)',
      'number_of_ppl_taking_shelter_in_night_shelters_cum': 'No. of people taking shelter in night shelter till now (Cum)',
      'number_of_blankets_distributed': 'No. of blankets distributed (Today)',
      'number_of_blankets_distributed_cum': 'No. of blankets distributed till now (Cum)'
    };

    const excelColumnLabels = [
      "District Name",
      "Block Name",
      "No. of Places where bonfire lit (Today)",
      "No. of Places where bonfire lit (Cum)",
      "Qty. of wood burned in kg (Today)",
      "Qty. of wood burned in kg till date (Cum)",
      "Loss of Life (Today)",
      "Loss of Life (Cum)",
      "No. of Night Shelters (Today)",
      "No. of Night Shelters till now (Cum)",
      "No. of people taking shelter in night shelter (Today)",
      "No. of people taking shelter in night shelter till now (Cum)",
      "No. of blankets distributed (Today)",
      "No. of blankets distributed till now (Cum)"
    ];

    const columns = excelColumnLabels.map(label => ({ label, key: label }));

    const transformedData = aggregatedBlocks.map((row) => {
      const transformedRow = {};
      
      Object.keys(fieldMapping).forEach(apiField => {
        const displayName = fieldMapping[apiField];
        const value = row[apiField];
        
        if (apiField.endsWith('_cum') || apiField === 'quantity_of_wood_burnt_till_now') {
          // ✅ REVERTED: Match Web UI - cumulative 0 shows as "-"
          transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : '-';
        } else if (apiField === 'dist_name' || apiField === 'block_name') {
          transformedRow[displayName] = value || '';
        } else {
          transformedRow[displayName] = value;
        }
      });
      
      return transformedRow;
    });

    const totalRow = { 
      'District Name': 'TOTAL',
      'Block Name': 'NA'
    };

    excelColumnLabels.forEach((columnLabel, idx) => {
      if (idx <= 1) return;

      let sum = 0;
      let validCount = 0;

      transformedData.forEach(row => {
        const value = row[columnLabel];
        if (value !== null && value !== undefined && value !== 'NA' && value !== '-' && value !== '' && !isNaN(value)) {
          sum += parseFloat(value);
          validCount++;
        }
      });

      if (validCount > 0) {
        totalRow[columnLabel] = sum;
      } else {
        totalRow[columnLabel] = 'NA';
      }
    });

    transformedData.push(totalRow);
    
    console.log(`✅ Final: ${transformedData.length - 1} block(s) + 1 TOTAL row`);
    
    return { columns, transformedData };
  }

  const districtData = rawData.district_summary || rawData.data || [];
  
  if (districtData.length === 0) {
    console.error('❌ No district summary data found');
    return { columns: [], transformedData: [] };
  }

  const fieldMapping = {
    'dist_name': 'District Name',
    'number_of_bonfire_places_cum': 'No. of Places where bonfire lit',
    'quantity_of_wood_burnt_cum': 'Qty. of wood burned in kg',
    'number_of_deaths_cum': 'Loss of Life',
    'number_of_night_shelters_cum': 'No. of Night Shelters',
    'number_of_ppl_taking_shelter_cum': 'No. of people taking shelter',
    'number_of_blankets_distributed_cum': 'No. of blankets distributed'
  };

  const columns = Object.values(fieldMapping).map(label => ({ label, key: label }));

  const transformedData = districtData.map(row => {
    const transformedRow = {};
    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      const value = row[apiField];
      transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : 'NA';
    });
    return transformedRow;
  });

  return { columns, transformedData };
};

/**
 * Transform MIS State Report (aggregated district data + TOTAL row)
 */
export const transformMISStateData = (rawData) => {
  console.log('🔍 transformMISStateData called');
  console.log('- Input type:', typeof rawData);
  console.log('- Is array?', Array.isArray(rawData));
  
  if (!rawData || typeof rawData !== 'object') {
    return { columns: [], transformedData: [] };
  }

  if (Array.isArray(rawData)) {
    console.log('✅ transformMISStateData: Aggregating by district');
    
    const filteredData = rawData.filter(row => {
      const distName = (
        row.dist_name ||
        row.District_Name ||
        row.distName ||
        row.district_name ||
        row.DIST_NAME ||
        ''
      ).toString().trim().toUpperCase();
      return distName !== 'TOTAL';
    });
    
    console.log(`✅ Processing ${filteredData.length} records`);

    const districtMap = {};
    
    filteredData.forEach(record => {
      const distKey = record.dist_name || record.district_name || 'Unknown';
      
      if (!districtMap[distKey]) {
        districtMap[distKey] = {
          dist_name: distKey,
          number_of_bonfire_places: 0,
          number_of_bonfire_places_cum: 0,
          quantity_of_wood_burnt: 0,
          quantity_of_wood_burnt_till_now: 0,
          number_of_deaths: 0,
          number_of_deaths_cum: 0,
          number_of_night_shelters: 0,
          number_of_night_shelters_cum: 0,
          number_of_ppl_taking_shelter_in_night_shelters: 0,
          number_of_ppl_taking_shelter_in_night_shelters_cum: 0,
          number_of_blankets_distributed: 0,
          number_of_blankets_distributed_cum: 0
        };
      }
      
      const dist = districtMap[distKey];
      
      // Today values - SUM
      dist.number_of_bonfire_places += (record.number_of_bonfire_places || 0);
      dist.quantity_of_wood_burnt += (record.quantity_of_wood_burnt || 0);
      dist.number_of_deaths += (record.number_of_deaths || 0);
      dist.number_of_night_shelters += (record.number_of_night_shelters || 0);
      dist.number_of_ppl_taking_shelter_in_night_shelters += (record.number_of_ppl_taking_shelter_in_night_shelters || 0);
      dist.number_of_blankets_distributed += (record.number_of_blankets_distributed || 0);
      
      // Cum values - MAX
      dist.number_of_bonfire_places_cum = Math.max(
        dist.number_of_bonfire_places_cum,
        record.number_of_bonfire_places_cum || 0
      );
      dist.quantity_of_wood_burnt_till_now = Math.max(
        dist.quantity_of_wood_burnt_till_now,
        record.quantity_of_wood_burnt_till_now || 0
      );
      dist.number_of_deaths_cum = Math.max(
        dist.number_of_deaths_cum,
        record.number_of_deaths_cum || 0
      );
      dist.number_of_night_shelters_cum = Math.max(
        dist.number_of_night_shelters_cum,
        record.number_of_night_shelters_cum || 0
      );
      dist.number_of_ppl_taking_shelter_in_night_shelters_cum = Math.max(
        dist.number_of_ppl_taking_shelter_in_night_shelters_cum,
        record.number_of_ppl_taking_shelter_in_night_shelters_cum || 0
      );
      dist.number_of_blankets_distributed_cum = Math.max(
        dist.number_of_blankets_distributed_cum,
        record.number_of_blankets_distributed_cum || 0
      );
    });
    
    const aggregatedDistricts = Object.values(districtMap);
    console.log(`✅ Aggregated ${filteredData.length} records into ${aggregatedDistricts.length} districts`);

    const fieldMapping = {
      'dist_name': 'District Name',
      'number_of_bonfire_places': 'No. of Places where bonfire lit (Today)',
      'number_of_bonfire_places_cum': 'No. of Places where bonfire lit (Cum)',
      'quantity_of_wood_burnt': 'Qty. of wood burned in kg (Today)',
      'quantity_of_wood_burnt_till_now': 'Qty. of wood burned in kg till date (Cum)',
      'number_of_deaths': 'Loss of Life (Today)',
      'number_of_deaths_cum': 'Loss of Life (Cum)',
      'number_of_night_shelters': 'No. of Night Shelters (Today)',
      'number_of_night_shelters_cum': 'No. of Night Shelters till now (Cum)',
      'number_of_ppl_taking_shelter_in_night_shelters': 'No. of people taking shelter in night shelter (Today)',
      'number_of_ppl_taking_shelter_in_night_shelters_cum': 'No. of people taking shelter in night shelter till now (Cum)',
      'number_of_blankets_distributed': 'No. of blankets distributed (Today)',
      'number_of_blankets_distributed_cum': 'No. of blankets distributed till now (Cum)'
    };

    const excelColumnLabels = [
      "District Name",
      "No. of Places where bonfire lit (Today)",
      "No. of Places where bonfire lit (Cum)",
      "Qty. of wood burned in kg (Today)",
      "Qty. of wood burned in kg till date (Cum)",
      "Loss of Life (Today)",
      "Loss of Life (Cum)",
      "No. of Night Shelters (Today)",
      "No. of Night Shelters till now (Cum)",
      "No. of people taking shelter in night shelter (Today)",
      "No. of people taking shelter in night shelter till now (Cum)",
      "No. of blankets distributed (Today)",
      "No. of blankets distributed till now (Cum)"
    ];

    const columns = excelColumnLabels.map(label => ({ label, key: label }));

    const transformedData = aggregatedDistricts.map(row => {
      const transformedRow = {};
      
      Object.keys(fieldMapping).forEach(apiField => {
        const displayName = fieldMapping[apiField];
        const value = row[apiField];
        
        if (apiField.endsWith('_cum') || apiField === 'quantity_of_wood_burnt_till_now') {
          // ✅ REVERTED: Match Web UI - cumulative 0 shows as "-"
          transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : '-';
        } else if (apiField === 'dist_name') {
          transformedRow[displayName] = value || '';
        } else {
          transformedRow[displayName] = value;
        }
      });
      
      return transformedRow;
    });

    const totalRow = { 'District Name': 'TOTAL' };

    excelColumnLabels.forEach((columnLabel, idx) => {
      if (idx === 0) return;

      let sum = 0;
      let validCount = 0;

      transformedData.forEach(row => {
        const value = row[columnLabel];
        if (value !== null && value !== undefined && value !== 'NA' && value !== '-' && value !== '' && !isNaN(value)) {
          sum += parseFloat(value);
          validCount++;
        }
      });

      if (validCount > 0) {
        totalRow[columnLabel] = sum;
      } else {
        totalRow[columnLabel] = 'NA';
      }
    });

    transformedData.push(totalRow);
    
    console.log(`✅ Final: ${transformedData.length - 1} district(s) + 1 TOTAL row`);
    
    return { columns, transformedData };
  }

  const stateData = rawData.state_summary || rawData.data || {};
  
  const fieldMapping = {
    'state_name': 'State Name',
    'number_of_bonfire_places_cum': 'No. of Places where bonfire lit',
    'quantity_of_wood_burnt_cum': 'Qty. of wood burned in kg',
    'number_of_deaths_cum': 'Loss of Life',
    'number_of_night_shelters_cum': 'No. of Night Shelters',
    'number_of_ppl_taking_shelter_cum': 'No. of people taking shelter',
    'number_of_blankets_distributed_cum': 'No. of blankets distributed'
  };

  const columns = Object.values(fieldMapping).map(label => ({ label, key: label }));

  const transformedRow = {};
  Object.keys(fieldMapping).forEach(apiField => {
    const displayName = fieldMapping[apiField];
    const value = stateData[apiField];
    transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : 'NA';
  });

  return { columns, transformedData: [transformedRow] };
};

/**
 * Transform MIS State Datewise Report
 */
export const transformMISStateDatewiseData = (rawData) => {
  console.log('transformMISStateDatewiseData called');
  
  if (!rawData || typeof rawData !== 'object') {
    return { columns: [], transformedData: [] };
  }

  let datewiseData;
  if (Array.isArray(rawData)) {
    console.log('✅ Detected direct array format (state datewise records)');
    datewiseData = rawData;
  } else {
    datewiseData = rawData.state_datewise || rawData.data;
  }

  if (!datewiseData || datewiseData.length === 0) {
    console.error('❌ No state datewise data found!');
    return { columns: [], transformedData: [] };
  }

  console.log(`✅ Processing ${datewiseData.length} state datewise records`);

  const fieldMapping = {
    'reported_on': 'Date',
    'dist_name': 'District Name',
    'number_of_bonfire_places': 'No. of Places where bonfire lit (Today)',
    'number_of_bonfire_places_cum': 'No. of Places where bonfire lit (Cum)',
    'quantity_of_wood_burnt': 'Qty. of wood burned in kg (Today)',
    'quantity_of_wood_burnt_till_now': 'Qty. of wood burned in kg (Cum)',
    'number_of_deaths': 'Loss of Life (Today)',
    'number_of_deaths_cum': 'Loss of Life (Cum)',
    'number_of_night_shelters': 'No. of Night Shelters (Today)',
    'number_of_night_shelters_cum': 'No. of Night Shelters (Cum)',
    'number_of_ppl_taking_shelter_in_night_shelters': 'No. of people taking shelter (Today)',
    'number_of_ppl_taking_shelter_in_night_shelters_cum': 'No. of people taking shelter (Cum)',
    'number_of_blankets_distributed': 'No. of blankets distributed (Today)',
    'number_of_blankets_distributed_cum': 'No. of blankets distributed (Cum)'
  };

  const columns = Object.values(fieldMapping).map(label => ({ label, key: label }));

  const transformedData = datewiseData.map(row => {
    const transformedRow = {};
    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      
      if (apiField === 'reported_on') {
        transformedRow[displayName] = formatDateForMISExcel(row[apiField]);
      } else {
        const value = row[apiField];
        
        if (apiField.endsWith('_cum') || apiField === 'quantity_of_wood_burnt_till_now') {
          // ✅ FIXED: State Datewise shows cumulative 0 as "0" (different from District Datewise)
          transformedRow[displayName] = (value !== null && value !== undefined) ? value : '-';
        } else if (apiField === 'dist_name') {
          transformedRow[displayName] = value || '';
        } else {
          // Today columns: show actual value, 0 is VALID
          transformedRow[displayName] = (value !== null && value !== undefined) ? value : '';
        }
      }
    });
    return transformedRow;
  });

  transformedData.sort((a, b) => {
    const dateA = a['Date'] || '';
    const dateB = b['Date'] || '';
    return dateA.localeCompare(dateB);
  });

  console.log(`✅ Transformed ${transformedData.length} rows for state datewise Excel`);
  return { columns, transformedData };
};

